import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { BrandLogo, COMPANY_NAME, SOFTWARE_TAGLINE } from "@/lib/brand";
import {
  LayoutDashboard, ClipboardList, PlusCircle, UserCheck, PackageSearch,
  Boxes, Users, Tag, PhoneCall, FileBarChart, Settings as SettingsIcon,
  LogOut, ShieldCheck, Map, Upload, Bell, Briefcase, PackagePlus, Megaphone,
  Inbox,
} from "lucide-react";
import { ROLE_LABELS } from "@/lib/statusStyles";
import NotificationsBell from "@/components/NotificationsBell";
import { api } from "@/lib/api";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/my-approvals", label: "My Approvals", icon: Inbox, roles: ["admin","service_head","ccr","manager","spares"], badge: "approvals" },
  { to: "/my-jobs", label: "My Jobs", icon: Briefcase, roles: ["technician"] },
  { to: "/complaints", label: "Complaints", icon: ClipboardList, perm: "complaints.view" },
  { to: "/complaints/new", label: "New Complaint", icon: PlusCircle, perm: "complaints.add" },
  { to: "/product-requests", label: "Add Product", icon: PackagePlus, perm: "products.request_create" },
  { to: "/product-approval", label: "Product Approval", icon: ShieldCheck, perm: "products.review" },
  { to: "/sent-spare-details", label: "Sent Spare Details", icon: PackageSearch, perm: "parts_inventory.view" },
  { to: "/assignment", label: "Technician Assignment", icon: UserCheck, perm: "technicians.assign_jobs" },
  { to: "/parts-requests", label: "Parts Requests", icon: PackageSearch, roles: ["admin","service_head","spares","manager"] },
  { to: "/inventory", label: "Part Inventory", icon: Boxes, perm: "parts_inventory.view" },
  { to: "/technicians", label: "Technicians", icon: Users, perm: "technicians.view" },
  { to: "/dealers", label: "Dealers", icon: Users, perm: "dealers.view" },
  { to: "/products", label: "Products", icon: Boxes, perm: "products.view" },
  { to: "/map", label: "Map View", icon: Map, roles: ["admin","service_head","manager","ccr"] },
  { to: "/service-prices", label: "Service Price List", icon: Tag, perm: "service_rates.view" },
  { to: "/happy-calls", label: "Happy Calls", icon: PhoneCall, roles: ["admin","service_head","ccr"] },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/import", label: "Import", icon: Upload, roles: ["admin","service_head","manager"] },
  { to: "/reports", label: "Reports", icon: FileBarChart, perm: "reports.view" },
  { to: "/reports/dealer-performance", label: "Dealer Performance", icon: FileBarChart, perm: "reports.dealer_performance" },
  { to: "/promoter", label: "Promoter Portal", icon: Megaphone, perm: "promoters.view", external: true },
  { to: "/admin/users", label: "Users", icon: Users, perm: "users.view" },
  { to: "/admin/roles", label: "Roles & Permissions", icon: ShieldCheck, perm: "roles.view" },
  { to: "/admin/warranty", label: "Warranty Master", icon: ShieldCheck, perm: "warranty.view" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, perm: "settings.view" },
];

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [approvalsTotal, setApprovalsTotal] = React.useState(0);

  // Poll My Approvals count (every 60s) for the sidebar badge.
  React.useEffect(() => {
    if (!user) return;
    if (user.role === "technician") return; // technicians have no approval responsibilities
    let active = true;
    const fetchCount = () => api.get("/my-approvals/summary")
      .then((r) => { if (active) setApprovalsTotal(r.data?.total ?? 0); })
      .catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  const isTechnician = user?.role === "technician";
  const isSpares = user?.role === "spares";
  const isSales = user?.role === "sales";

  // Strictly limited mobile-first nav for technicians — no admin/sidebar/company items.
  const TECH_NAV = [
    { to: "/technician/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/my-jobs", label: "My Jobs", icon: Briefcase },
    { to: "/technician/reports", label: "Reports", icon: FileBarChart },
    { to: "/technician/parts-ledger", label: "Parts Ledger", icon: PackageSearch },
  ];

  // Spares Assistant navigation — permission-gated where applicable.
  // 'Part Inventory' is the unified inventory + stock entry module.
  const SPARES_NAV = [
    { to: "/spares/dashboard", label: "Spares Dashboard", icon: LayoutDashboard },
    { to: "/parts-requests", label: "Parts Requests", icon: PackageSearch },
    { to: "/spares/parts-entry", label: "Part Inventory", icon: Boxes, perm: "parts_inventory.view" },
    { to: "/spares/mrn", label: "MRN", icon: PlusCircle },
    { to: "/spares/part-takeout", label: "Part Takeout", icon: PackageSearch, perm: "parts_inventory.takeout" },
    { to: "/spares/pods", label: "PODs", icon: FileBarChart },
    { to: "/sent-spare-details", label: "Sent Spare Details", icon: PackageSearch, perm: "parts_inventory.view" },
    { to: "/spares/parts-taken-out-report", label: "Parts Taken-Out Report", icon: FileBarChart },
    { to: "/spares/part-approval-report", label: "Part Approval Report", icon: FileBarChart },
    { to: "/complaints", label: "Complaints (view)", icon: ClipboardList },
    { to: "/notifications", label: "Notifications", icon: Bell },
  ];

  // Sales Team — strictly read-only navigation: dealer report, complaints/dealers/products view, reports.
  const SALES_NAV = [
    { to: "/dashboard", label: "Operations Dashboard", icon: LayoutDashboard },
    { to: "/reports/dealer-performance", label: "Dealer Performance", icon: FileBarChart },
    { to: "/complaints", label: "Complaints (view)", icon: ClipboardList },
    { to: "/dealers", label: "Dealers", icon: Users },
    { to: "/products", label: "Products", icon: Boxes },
    { to: "/reports", label: "Reports", icon: FileBarChart },
    { to: "/notifications", label: "Notifications", icon: Bell },
  ];

  // Permission/role gate. If `perm` is set, hide unless caller has it. If `roles` is set, hide unless caller's role matches.
  const allowed = (item) => {
    if (item.perm && !can(item.perm)) return false;
    if (item.roles && !item.roles.includes(user?.role) && user?.role !== "admin") return false;
    return true;
  };

  let visible;
  if (isTechnician) visible = TECH_NAV;
  else if (isSpares) visible = SPARES_NAV.filter(allowed);
  else if (isSales) visible = SALES_NAV;
  else visible = NAV.filter(allowed);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  // ----- Technician mobile shell: no sidebar, simple bottom-nav -----
  if (isTechnician) {
    return (
      <div className="min-h-screen bg-slate-50 pb-16" data-testid="app-shell-tech">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <BrandLogo size={32} testId="brand-logo-tech-header" />
            <div className="min-w-0">
              <div className="font-heading font-bold text-sm leading-tight truncate">{COMPANY_NAME}</div>
              <div className="text-[10px] text-slate-500 truncate" data-testid="tech-user-name">{user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NotificationsBell />
            <button onClick={handleLogout} className="px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 rounded" data-testid="logout-button">
              <LogOut size={14} />
            </button>
          </div>
        </header>

        <main className="px-3 py-4">{children}</main>

        <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 grid grid-cols-4 z-40" data-testid="tech-bottom-nav">
          {TECH_NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to}
                       className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 text-[10px] ${isActive ? "text-slate-900 font-semibold" : "text-slate-500"}`}
                       data-testid={`tech-nav-${item.to.replaceAll("/", "-").replace(/^-/, "")}`}>
                <Icon size={18} strokeWidth={2} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 lg:flex" data-testid="app-shell">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3">
        <button onClick={() => setMobileOpen(!mobileOpen)} className="text-slate-700 px-2 py-1 border border-slate-200 rounded-md" data-testid="mobile-menu-toggle">☰</button>
        <div className="flex items-center gap-2">
          <BrandLogo size={28} testId="brand-logo-mobile" />
          <div className="font-heading font-bold text-sm">{COMPANY_NAME}</div>
        </div>
        <NotificationsBell />
      </div>

      <aside className={`${mobileOpen ? "block" : "hidden"} lg:block w-full lg:w-64 bg-white border-r border-slate-200 flex flex-col`} data-testid="sidebar">
        <div className="px-5 py-5 border-b border-slate-200 hidden lg:flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandLogo size={36} testId="brand-logo-sidebar" />
            <div>
              <div className="font-heading font-bold text-[15px] leading-tight text-slate-900">{COMPANY_NAME}</div>
              <div className="text-[11px] tracking-[0.08em] uppercase text-slate-500 font-medium">{SOFTWARE_TAGLINE}</div>
            </div>
          </div>
          <NotificationsBell />
        </div>

        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
          {visible.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/dashboard"}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${item.to.replaceAll("/", "-").replace(/^-/, "")}`}
              >
                <Icon size={16} strokeWidth={2} />
                <span className="flex-1">{item.label}</span>
                {item.badge === "approvals" && approvalsTotal > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center text-[10px] font-bold bg-rose-600 text-white rounded-full min-w-[18px] h-[18px] px-1"
                        data-testid="approvals-badge">
                    {approvalsTotal > 99 ? "99+" : approvalsTotal}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-slate-200">
          <div className="px-3 pb-2">
            <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold">{ROLE_LABELS[user?.role] || user?.role}</div>
            <div className="text-sm font-medium text-slate-900 truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-md transition"
            data-testid="logout-button"
          >
            <LogOut size={15} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-[1600px] px-4 sm:px-6 py-6">{children}</div>
      </main>
    </div>
  );
}
